import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { siniestrosService } from '../services/siniestrosService';

const TIPOS_BUSQUEDA = [
  { value: 'general',   label: 'Búsqueda general' },
  { value: 'titular',   label: 'Titular' },
  { value: 'siniestro', label: 'Nº Siniestro' },
  { value: 'poliza',    label: 'Póliza' },
  { value: 'telefono',  label: 'Teléfono' },
  { value: 'domicilio', label: 'Domicilio' },
  { value: 'albaran',   label: 'Albarán' },
];

function Badge({ activo, texto, color = 'blue' }) {
  if (!activo) return null;
  const colores = {
    green:  'bg-green-100 text-green-800',
    red:    'bg-red-100 text-red-800',
    yellow: 'bg-yellow-100 text-yellow-900',
    blue:   'bg-blue-100 text-blue-800',
    gray:   'bg-gray-100 text-gray-600',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${colores[color]}`}>
      {texto}
    </span>
  );
}

function EstadoBadges({ s }) {
  if (s.anulado)   return <Badge activo texto="Anulado"   color="red" />;
  if (s.cobrado)   return <Badge activo texto="Cobrado"   color="green" />;
  if (s.facturado) return <Badge activo texto="Facturado" color="yellow" />;
  if (s.emitido)   return <Badge activo texto="Emitido"   color="blue" />;
  return <Badge activo texto="Pendiente" color="gray" />;
}

export default function SiniestrosList() {
  const navigate = useNavigate();
  const [datos, setDatos]         = useState({ content: [], totalElements: 0, totalPages: 0 });
  const [cargando, setCargando]   = useState(false);
  const [error, setError]         = useState(null);
  const [tipoBusq, setTipoBusq]   = useState('general');
  const [valorBusq, setValorBusq] = useState('');
  const [soloPend, setSoloPend]   = useState(false);
  const [pagina, setPagina]       = useState(0);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      let res;
      if (soloPend) {
        res = await siniestrosService.pendientes(null, null, pagina);
      } else if (valorBusq.trim()) {
        res = await siniestrosService.buscar(tipoBusq, valorBusq.trim(), pagina);
      } else {
        res = await siniestrosService.listar(pagina);
      }
      setDatos(res.data);
    } catch (e) {
      setError('Error al conectar con el servidor');
    } finally {
      setCargando(false);
    }
  }, [tipoBusq, valorBusq, soloPend, pagina]);

  useEffect(() => { cargar(); }, [cargar]);

  const buscar = (e) => {
    e.preventDefault();
    setPagina(0);
    cargar();
  };

  const limpiar = () => {
    setValorBusq('');
    setSoloPend(false);
    setPagina(0);
    setTipoBusq('general');
  };

  return (
    <div className="min-h-screen bg-gray-50">

      <div className="max-w-7xl mx-auto px-4 py-4 space-y-4">

        {/* ── Título + botón nuevo ── */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-800">Siniestros
            <span className="ml-2 text-sm font-normal text-gray-400">{datos.totalElements} resultados</span>
          </h2>
          <button
            onClick={() => navigate('/siniestros/nuevo')}
            className="bg-primary-600 text-white font-semibold px-4 py-2 rounded-lg text-sm hover:bg-primary-700 transition"
          >
            + Nuevo
          </button>
        </div>

        {/* ── Buscador ── */}
        <form onSubmit={buscar} className="bg-white rounded-xl shadow-sm p-4 space-y-3">
          <div className="flex gap-2 min-w-0">
            <select
              value={tipoBusq}
              onChange={e => setTipoBusq(e.target.value)}
              className="w-28 sm:w-auto shrink-0 border border-gray-300 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
            >
              {TIPOS_BUSQUEDA.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            <input
              type="text"
              value={valorBusq}
              onChange={e => setValorBusq(e.target.value)}
              placeholder="Buscar..."
              aria-label="Texto de búsqueda"
              className="flex-1 min-w-0 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <button
              type="submit"
              aria-label="Buscar"
              className="shrink-0 bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 transition"
            >
              🔍
            </button>
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={soloPend}
                onChange={e => { setSoloPend(e.target.checked); setPagina(0); }}
                className="rounded"
              />
              Solo pendientes
            </label>
            {(valorBusq || soloPend) && (
              <button
                type="button"
                onClick={limpiar}
                className="text-sm text-gray-400 hover:text-gray-600 underline"
              >
                Limpiar filtros
              </button>
            )}
          </div>
        </form>

        {/* ── Error ── */}
        {error && (
          <div role="alert" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* ── Lista ── */}
        {cargando ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : (
          <div className="space-y-2">
            {datos.content.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400">
                No se encontraron siniestros
              </div>
            ) : (
              datos.content.map(s => (
                <div
                  key={s.id}
                  onClick={() => navigate(`/siniestros/${s.id}`)}
                  className="bg-white rounded-xl shadow-sm p-4 cursor-pointer hover:shadow-md hover:border-primary-200 border border-transparent transition active:scale-95"
                >
                  {/* Fila superior: nº siniestro + póliza */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900 text-sm">
                          {s.nSiniestro || '(sin número)'}
                        </span>
                        {s.extension > 1 && (
                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                            Ext. {s.extension}
                          </span>
                        )}
                        {s.codPoliza && (
                          <span className="text-xs text-gray-500">Póliza: {s.codPoliza}</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-700 mt-0.5 truncate">
                        {s.titular || '(sin titular)'}
                      </p>
                    </div>
                    <EstadoBadges s={s} />
                  </div>

                  {/* Fila inferior */}
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                    {s.domicilio && (
                      <span>{s.domicilio}{s.localidad ? ` · ${s.localidad}` : ''}</span>
                    )}
                    {s.telefono1 && (
                      <a
                        href={`tel:${s.telefono1}`}
                        onClick={e => e.stopPropagation()}
                        className="text-primary-600 hover:underline"
                      >
                        📞 {s.telefono1}
                      </a>
                    )}
                    {s.perito && (
                      <span>👤 {s.perito}</span>
                    )}
                    {(s.fechaFacturado || s.fechaAsignacion) && (
                      <span>📅 {new Date(s.fechaFacturado || s.fechaAsignacion).toLocaleDateString('es-ES')}</span>
                    )}
                    {s.albaran && (
                      <span>🧾 Alb. {s.albaran}</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* ── Paginación ── */}
        {datos.totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 py-4">
            <button
              onClick={() => setPagina(p => Math.max(0, p - 1))}
              disabled={pagina === 0}
              className="px-4 py-2 rounded-lg border border-gray-300 text-sm disabled:opacity-40 hover:bg-gray-50"
            >
              ← Anterior
            </button>
            <span className="text-sm text-gray-600">
              {pagina + 1} / {datos.totalPages}
            </span>
            <button
              onClick={() => setPagina(p => Math.min(datos.totalPages - 1, p + 1))}
              disabled={pagina >= datos.totalPages - 1}
              className="px-4 py-2 rounded-lg border border-gray-300 text-sm disabled:opacity-40 hover:bg-gray-50"
            >
              Siguiente →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
